import * as React from 'react';
import * as _ from 'underscore';

import { IReactTranslateService } from '../IReactTranslateService';

/**
 * @deprecated use `StringInterpolation.ts` instead
 *
 * InterpolatedString is a way of injecting react elements into strings.
 *
 * An example use case is injecting spans or anchors into localized strings.
 *
 * ex:
 * "{opt1} or {opt2}"
 *         |
 *         |
 *         v
 * <span><a href="examplevideo.html">Watch a video</a> or <a onClick="startDemo()">try it yourself</a></span>
 *
 *
 * usage example:
 *
 * const key = 'helloWorld' // translate knows of this as "Hello {WORLD}!"
 * const replacements = { 'WORLD': () => <a onClick={sendMonitoringEventFn} >World</a> }
 * <DeprecatedInterpolatedString baseStringKey={key} replacements={replacements} translate={() => Messages.foo()}/>
 *
 * results in: <span>Hello, <a onClick="sendMonitoringEventFn" >World</a></span>
 *
 */
export namespace DeprecatedInterpolatedString {

  export interface Props {
    baseStringKey: string;
    translate: IReactTranslateService;
    replacements: _.Dictionary<React.Factory<void>>;
    containerClassName?: string;
    replacementPartClassName?: string;
  }
}

function wrapInBraces(str: string) {
  return '{' + str + '}';
}

// Used internally, defined for clarity.
interface ReplacementInString {
  frontIndex: number;
  backIndex: number;
  replaceWith: React.ReactElement<any>;
}

export class DeprecatedInterpolatedString extends React.Component<DeprecatedInterpolatedString.Props, {}> {

  public render() {
    const baseString = tokenPreservingTranslate(this.props.baseStringKey, _.keys(this.props.replacements), this.props.translate);

    const replacementsInOrder: ReplacementInString[] = _.chain(this.props.replacements)
      .map((replacer: React.Factory<void>, toReplace: string) => this.findReplacementInString(toReplace, replacer, baseString))
      .filter((replacementInString) => replacementInString.frontIndex >= 0)
      .sortBy('frontIndex')
      .value();

    const resultParts: (React.ReactElement<any> | String)[] = [];
    let substringStart = 0;

    _.each(replacementsInOrder, (replacementInString: ReplacementInString) => {
      const substr: string = baseString.substring(substringStart, replacementInString.frontIndex);
      if (substr) resultParts.push(substr);
      resultParts.push(replacementInString.replaceWith);
      substringStart = replacementInString.backIndex;
    });

    const restOfString = baseString.substring(substringStart);
    if (restOfString) resultParts.push(restOfString);

    return <span className={this.props.containerClassName}>{ resultParts }</span>;
  }

  private findReplacementInString(keyToReplace: string, replacer: React.Factory<void>, baseString: string): ReplacementInString {
    const keyInBraces = wrapInBraces(keyToReplace);

    const frontIndex = baseString.indexOf(keyInBraces);
    const backIndex = frontIndex + keyInBraces.length;
    const replaceWith = <span className={this.props.replacementPartClassName} key={ keyToReplace }>{ (replacer()) }</span>;

    return { frontIndex, backIndex, replaceWith };
  }

}

/*
 * IReactTranslateService (translate) already has some built-in interpolation abilities for strings.
 *
 * We use translate in order to do our translation, but we don't want to use its string interpolation
 * (cause we're running our own here).
 *
 * This function causes translate's interpolation to be a no op, allowing us to preserve our interpolation
 * tokens.
 *
 * It's exported so that it can be tested directly.
 *
 * As of 2/2017, there's no support for fancy message format strings.
 * ex: {ERROR_COUNT, plural, one{{ERROR_COUNT_LOC} alert} other{{ERROR_COUNT_LOC} alerts}}
 * If you want to use message format, add some optional props and wire it through to the translate call here.
 *
 */
export function tokenPreservingTranslate(baseStringKey: string, tokensToIgnore: string[], translate: IReactTranslateService): string {
  const tokenPreserver = _.object(tokensToIgnore, _.map(tokensToIgnore, wrapInBraces)); // looks like { 'TOKEN': '{TOKEN}' }

  return translate(baseStringKey, tokenPreserver);
}

